<template>
  <div class="account-wrap">
    <div class="account-left-wrap">
      <div class="plan">
        <div class="account-plan-image">
          <img
            src="../../../assets/img/account/plan/plan1.svg"
            alt=""
          >
        </div>
        <div>
          <div class="plan__your-plan">
            {{ $t('plan') }}
          </div>
          <div class="plan__name-wrap">
            <div class="plan__plan-name">
              {{ account.plan.title }}
            </div>
            <button v-if="plan.default" @click="changePlan" class="plan__change">
              <svg
                width="22px"
                height="22px"
                viewBox="0 0 22 22"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M5 14.5017V17.0017H7.5L14.8733 9.62833L12.3733 7.12833L5 14.5017ZM16.8067 7.695C17.0667 7.435 17.0667 7.015 16.8067 6.755L15.2467 5.195C14.9867 4.935 14.5667 4.935 14.3067 5.195L13.0867 6.415L15.5867 8.915L16.8067 7.695Z" fill="#0A82D9" />
              </svg>
              <span class="d-none d-sm-inline">{{ $t('change') }}</span>
            </button>
          </div>
          <div class="plan__activity-wrap">
            <div v-if="active" class="active plan__activity-status">
              {{ $t('active') }}
            </div>
            <div v-else class="suspended plan__activity-status">
              {{ $t('disabled') }}
            </div>
            <div class="period plan__activity-period">
              {{ $t('currentPeriod') }}: {{ isExpired ? $t('subscriptionExpired') : periodText }}
            </div>
            <div class="period plan__activity-period" v-if="!isExpired && !plan.default">
              {{ $t('payedUntil') }}: {{ expirationText }}
            </div>
          </div>
        </div>
      </div>
      <div class="account-quotas">
        <div class="account-quotas__title">
          {{ $t('left') }}:
        </div>
        <PlanQuotaItems />
      </div>
      <div class="account-quotas-ended">
        <PaymentDashboardProlongSubscription />
        <InterviewsQuotaExceeded v-if="quota && !quota.interviews && subscriptionStatus" />
      </div>
    </div>
    <div class="account-right-wrap">
      <PaymentDashboardDetails />
    </div>
  </div>
</template>

<script>
import useAccount from '@/compositions/accounting/useAccount';
import InterviewsQuotaExceeded from '@/components/Payment/Quota/InterviewsQuotaExceeded';
import { paymentTabs, settingsTabs } from '@/enums';
import PaymentDashboardDetails from '@/components/Payment/Dashboard/PaymentDashboardDetails';
import useSubscription from '@/compositions/accounting/useSubscription';
import PlanQuotaItems from '@/components/Payment/Plans/PlanQuotaItems';
import PaymentDashboardProlongSubscription from '@/components/Payment/Dashboard/PaymentDashboardProlongSubscription';

export default {
  name: 'PaymentDashboard',
  components: {
    PaymentDashboardProlongSubscription,
    PlanQuotaItems,
    PaymentDashboardDetails,
    InterviewsQuotaExceeded,
  },
  setup: (_, { root: { $router } }) => {
    const {
      account, periodText, quota, expirationText,
    } = useAccount();
    const { subscriptionStatus, isExpired } = useSubscription();
    const {
      plan, period, active,
    } = account.value;

    function changePlan() {
      $router.push({ name: 'settings', params: { type: settingsTabs.PAYMENT, addType: paymentTabs.PLANS } });
    }

    return {
      plan,
      period,
      account,
      periodText,
      active,
      quota,
      changePlan,
      subscriptionStatus,
      isExpired,
      expirationText,
    };
  },
};
</script>

<style lang="scss" scoped>
  .account-wrap {
    width: 100%;
    display: flex;
    align-items: stretch;
    color: #212121;
    font-weight: 400;
  }
  .account-left-wrap {
    flex-grow: 1;
    padding: 2.5rem 2rem 2.5rem 2.5rem;
  }
  .account-right-wrap {
    width: 30.25rem;
    padding: 1.25rem;
    flex-shrink: 0;
  }
  .account-plan-image {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 7.5rem;
    height: 7.5rem;
    border-radius: 50%;
    background: rgba(10, 130, 217, 0.06);
    border: 1px solid rgba(10, 130, 217, 0.1);
    margin-right: 2.5rem;
    img {
      max-width: 100%;
      max-height: 100%;
    }
  }
  .plan {
    display: flex;
    &__your-plan {
      font-size: 0.875rem;
      line-height: 1.2;
      margin-bottom: 0.25rem;
    }
    &__name-wrap {
      display: flex;
      align-items: flex-end;
      margin-bottom: 0.5rem;
    }
    &__plan-name {
      font-size: 2rem;
      line-height: 1.2;
    }
    &__change {
      font-size: 0.875rem;
      line-height: 1.6;
      color: #0A82D9;
      margin-left: 0.5rem;
      padding-bottom: 0.25rem;
      svg {
        display: inline-block;
        vertical-align: middle;
      }
    }
    &__activity-status {
      position: relative;
      margin-bottom: 0.25rem;
      &:before {
        content: '';
        display: inline-block;
        width: 0.375rem;
        height: 0.375rem;
        border-radius: 50%;
        margin-right: 0.5rem;
        vertical-align: middle;
      }
      &.active:before {
        background-color: #1EC658;
      }
      &.suspended:before {
        background-color: #FF0000;
      }
    }
    &__activity-period {
      padding-left: 1rem;
      font-size: 0.875rem;
      line-height: 1.57;
      color: #757575;
      margin-bottom: 0.875rem;
    }
  }
  .account-quotas {
    max-width: 37rem;
    padding-left: 10rem;
    &__title {
      font-size: 1.125rem;
      line-height: 1.22;
      margin-bottom: 1.5rem;
    }
    &__wrap {
      display: flex;
    }
  }
  .account-quotas-ended {
    max-width: 37rem;
    padding-left: 10rem;
  }

  @media #{map-get($display-breakpoints, 'md-and-down')} {
  .account-wrap {
    display: block;
  }
  .account-plan-image {
    min-width: 5.625rem;
    width: 5.625rem;
    height: 5.625rem;
    padding: 1rem;
    margin-right: 1.75rem;
  }
  .plan {
      &__plan-name {
        font-size: 1.75rem;
      }
      &__change {
        margin-left: 0.25rem;
      }
      &__activity-status {
        font-size: 0.9375rem;
      }
      &__activity-period {
        font-size: 0.9375rem;
      }
    }
  .account-quotas {
    max-width: 34rem;
    padding-left: 7.375rem;
    &__title {
      margin-bottom: 1.25rem;
    }
  }
  .account-quotas-ended {
    padding-left: 0;
  }
  .account-right-wrap {
    width: 100%;
    padding: 2rem 1.875rem 2.5rem;
  }
}
  @media #{map-get($display-breakpoints, 'xs-only')} {
    .account-left-wrap {
      flex-wrap: wrap;
      padding: 1.25rem;
    }
    .account-plan-image {
      min-width: 3.75rem;
      width: 3.75rem;
      height: 3.75rem;
      padding: 0.625rem;
      margin-right: 0.9375rem;
    }
    .plan {
      &__your-plan {
        font-size: 0.8125rem;
        line-height: 1.4;
        margin-bottom: 0;
      }
      &__name-wrap {
        margin-bottom: 0.625rem;
      }
      &__plan-name {
        font-size: 1.375rem;
      }
      &__change {
        margin-left: 0;
        padding-bottom: 0;
      }
      &__activity-status {
        font-size: 0.8125rem;
      }
      &__activity-period {
        font-size: 0.8125rem;
      }
    }
    .account-quotas {
      max-width: 22rem;
      padding-left: 0;
      &__title {
        font-size: 1rem;
        line-height: 1.125;
        margin-bottom: 0.625rem;
      }
    }
    .account-right-wrap {
      padding: 0 0 2rem 0;
    }
  }

</style>

<i18n lang="yml" locale="ru">
plan:  Ваш тарифный план
active: Активен
disabled: Неактивен
left: Ваш баланс
currentPeriod: Текущий период
payedUntil: Оплачен до
subscriptionExpired: Подписка закончилась
change: Изменить
</i18n>
<i18n lang="yml" locale="en">
plan: Your plan
active: Active
disabled: Expired
payedUntil: Paid until
left: You have
currentPeriod: Current period
subscriptionExpired: Subscription has expired
change: change
</i18n>
